import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  useContext,
} from "react";
import {
  MButton,
  MCheckBox,
  BufferService,
  MInputTextField, // design-system input
} from "src/M_Components";
import { LoadingContext } from "src/M_Components"; // used to obtain baseUrl (DEFAULTURL) and global loading overlay
import { useTranslation } from "react-i18next";
import "./SelectableTileGrid.css";

/* -------------------------------------------------------------------------- */
/*                                  <Tile/>                                   */
/*  Memoized item tile with fixed height to avoid layout jumps.               */
/* -------------------------------------------------------------------------- */
const Tile = memo(function Tile({
  label,
  checked,
  disabled,
  onToggle,
  tileHeight = 40,
}) {
  // Simple visual tile: label on the left, checkbox on the right
  return (
    <div
      className={`stg-tile ${disabled ? "disabled" : ""}`}
      style={{ height: tileHeight }}
    >
      <span className="stg-tile-label" title={label}>
        {label}
      </span>
      <MCheckBox checked={!!checked} onChange={onToggle} disabled={disabled} />
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/*                          Debounced primitive value                          */
/* -------------------------------------------------------------------------- */
// Small hook that delays value changes so we do not spam the API while the user types
const useDebouncedValue = (value, delayMs = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(h);
  }, [value, delayMs]);
  return debounced;
};

/* -------------------------------------------------------------------------- */
/*                              Build server filter                            */
/*  Example: contains(Name,'Trans') and (Inactive eq false)                   */
/* -------------------------------------------------------------------------- */
// Builds an OData style filter string based on search text and an optional static filter
function buildFilter({ displayField, queryText, staticFilter }) {
  const parts = [];
  if (queryText && queryText.trim()) {
    const q = queryText.trim().replace(/'/g, "''");
    parts.push(`contains(${displayField}, '${q}')`);
  }
  if (staticFilter && staticFilter.trim()) {
    parts.push(staticFilter.trim());
  }
  if (!parts.length) return null;
  return parts.join(" and ");
}

/* -------------------------------------------------------------------------- */
/*                           Auto min-height helper                            */
/* -------------------------------------------------------------------------- */
// Tries to give the container a reasonable minimum height so the layout does not jump too much
function computeAutoMinHeight({ pageSize, tileHeight, gridGap, showToolbar }) {
  const visibleItems = Math.min(10, Math.max(1, pageSize || 20));
  const toolbarH = showToolbar ? 56 : 0;
  const topBottomPadding = 20 * 2;
  const itemsBlock =
    visibleItems * tileHeight + Math.max(0, visibleItems - 1) * gridGap;
  return toolbarH + topBottomPadding + itemsBlock;
}

/* -------------------------------------------------------------------------- */
/*                       Safe JSON (storage) helpers                           */
/* -------------------------------------------------------------------------- */
// Tiny wrapper to read a JSON blob from storage without blowing up on malformed data
function safeRead(storage, key) {
  try {
    const raw = storage?.getItem?.(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
// Tiny wrapper to write a JSON blob to storage, ignoring quota / private mode errors
function safeWrite(storage, key, value) {
  try {
    storage?.setItem?.(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

/* -------------------------------------------------------------------------- */
/*                           SelectableTileGrid (main)                         */
/* -------------------------------------------------------------------------- */
/**
 * Props:
 * - title: string
 * - endpoint?: string                   // required in remote mode (viewMode === false)
 * - displayField: string                // e.g., "Name"
 * - idField?: string                    // default "Id"
 * - pageSize?: number                   // default 20
 * - queryable?: boolean                 // show/hide search when true/false (default true)
 * - orderable?: boolean                 // show/hide ASC/DESC when true/false (default true)
 * - initialOrderDir?: 'asc'|'desc'      // default 'asc'
 * - searchPlaceholder?: string
 * - staticFilter?: string               // extra server filter (ANDed with contains)
 * - selectableIds?: number[]            // whitelist of selectable ids; others disabled
 * - selectedIds?: number[]              // controlled selection from parent
 * - onItemToggle?: (item, next:boolean) => void
 * - disabled?: boolean
 * - viewMode?: boolean                  // if true, uses `items` and does LOCAL query/sort
 * - items?: any[]                       // list for viewMode
 * - transformItem?: (raw) => { id, label, raw } // optional normalizer
 * - width?: string|number               // container width (e.g., "100%")
 * - minHeight?: string|number           // min container height; auto if not provided
 * - maxHeight?: string|number
 * - gridGap?: number                    // spacing between tiles (default 12)
 * - tileHeight?: number                 // FIXED height for each tile (default 40)
 * - initialState?: { query?: string, orderDir?: 'asc'|'desc', pages?: number }
 *
 * New:
 * - stateScopeId?: string               // explicit scope to share state on purpose
 * - enableInternalPersistence?: boolean // opt-in sessionStorage persistence (default false)
 * - storage?: Storage                   // custom storage (defaults to window.sessionStorage)
 */
export default function SelectableTileGrid({
  title,
  endpoint,
  displayField,
  idField = "Id",
  pageSize = 50,
  queryable = true,
  orderable = true,
  initialOrderDir = "asc",
  initialState, // normalized: { orderDir?: 'asc'|'desc', query?: string, pages?: number }
  onStateChange,
  stateScopeId, // resets tab internal state when changed (and for persistence)
  searchPlaceholder,
  staticFilter,
  selectableIds,
  selectedIds = [],
  onItemToggle,
  disabled = false,
  viewMode = false,
  items: viewItems = [],
  transformItem,
  width = "100%",
  minHeight,
  maxHeight,
  gridGap = 12,
  tileHeight = 40,
  // ---------- Optional knobs for self-contained persistence/isolation ----------
  enableInternalPersistence = false,
  storage,
  align = "auto", // "auto" | "vertical"
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const baseUrl = ctx?.DEFAULTURL || "";

  const effectiveTileHeight = align === "vertical" ? 80 : tileHeight;

  // Resolve storage (sessionStorage gives per-browser-tab isolation)
  // This is kept in a ref so we do not re-resolve storage on every render
  const storageRef = useRef(null);
  useEffect(() => {
    storageRef.current =
      storage || (typeof window !== "undefined" ? window.sessionStorage : null);
  }, [storage]);

  /* ----------------------------- Instance-scope id ---------------------------- */
  // Prevent unintended cross-bleed: each component instance gets a stable random id.
  // If you WANT shared state across instances, pass a stateScopeId prop.
  const instanceIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `inst_${Math.random().toString(36).slice(2)}`
  );

  /* ----------------------------- Effective scope id --------------------------- */
  // This scope id is what we use to separate persisted state per logical grid
  const effectiveScopeId =
    stateScopeId ??
    `${endpoint || "no-endpoint"}::${displayField || "fld"}::${instanceIdRef.current
    }`;

  /* ----------------------------- UI / query state ----------------------------- */
  // Order direction, local query text and its debounced version
  const [orderDir, setOrderDir] = useState(
    initialOrderDir === "desc" ? "desc" : "asc"
  );
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  /* -------------------------- Accumulated list + paging ----------------------- */
  // Remote items represent what has been fetched from the server so far (paged)
  const [items, setItems] = useState([]); // remote items (for API mode)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false); // INTERNAL flag (not rendered); drives ctx.Loading
  const [errorMsg, setErrorMsg] = useState("");

  // Controls remote fetch lifecycle
  const abortRef = useRef(null);
  const requestTokenRef = useRef(0); // increases on each fetch; ignore late responses
  const appliedInitialRef = useRef(false); // controls 1x apply (initialState or storage)
  const lastScopeIdRef = useRef(null);

  /* ------------------------------ Item normalizer ----------------------------- */
  // Maps any server or local item into a simple { id, label, raw } shape
  const mapItem = useCallback(
    (raw) => {
      if (typeof transformItem === "function") {
        const mapped = transformItem(raw);
        return {
          id: mapped?.id ?? raw?.[idField],
          label:
            mapped?.label ??
            raw?.[displayField] ??
            String(raw?.[idField] ?? ""),
          raw: mapped?.raw ?? raw,
        };
      }
      return {
        id: raw?.[idField],
        label: raw?.[displayField] ?? String(raw?.[idField] ?? ""),
        raw,
      };
    },
    [transformItem, idField, displayField]
  );

  /* --------------------------- Selectable (whitelist) ------------------------- */
  /**
   * How the whitelist behaves:
   * - selectableIds is undefined / not passed:
   *       -selectableSet = null
   *       -means "no whitelist": all items are allowed (default behavior).
   *
   * - selectableIds is an empty array []:
   *      -selectableSet = new Set([])  (empty Set)
   *      -means "explicit whitelist with zero ids": no item is allowed.
   *
   * - selectableIds has values (ex: [1, 2, 3]):
   *      -only those ids are allowed to be interacted with.
   *
   * This distinction is important:
   *   - "no prop" : acts as if there is no filter at all.
   *   - "[]" (empty arr) : acts as "block everything".
   */
  const selectableSet = useMemo(() => {
    // If the prop is not an array (undefined / null), we treat it as "no whitelist".
    // In that case we return null and later we allow all items.
    if (!Array.isArray(selectableIds)) return null;

    // If the prop is an array (even an empty one), we create a Set out of it.
    // - [] -> Set() with size 0 -> selectableSet.has(id) is always false -> everything disabled.
    // - [1, 2, 3] -> Set(1,2,3)  -> only ids 1,2,3 are allowed.
    return new Set(selectableIds);
  }, [selectableIds]);

  // Selected items are still controlled from the outside via selectedIds.
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  /* ------------------------------ Toolbar flags ------------------------------- */
  // These flags drive whether the search box and order button should be visible
  const showSearch = !viewMode && !!queryable;
  const showOrder = !viewMode && !!orderable;
  const showToolbar = showSearch || showOrder;

  /* -------------------------- LOCAL (viewMode) LIST --------------------------- */
  // When in viewMode, we do not talk to the server and we filter/sort locally
  const localList = useMemo(() => {
    if (!viewMode) return [];
    // Normalize first
    let list = (Array.isArray(viewItems) ? viewItems : []).map(mapItem);
    // Local filter (when queryable)
    if (queryable && debouncedQuery.trim()) {
      const q = debouncedQuery.trim().toLowerCase();
      list = list.filter((x) => `${x.label ?? ""}`.toLowerCase().includes(q));
    }
    // Local sort (when orderable)
    if (orderable) {
      list.sort((a, b) => {
        const A = `${a.label ?? ""}`.toLowerCase();
        const B = `${b.label ?? ""}`.toLowerCase();
        if (A < B) return orderDir === "asc" ? -1 : 1;
        if (A > B) return orderDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [
    viewMode,
    viewItems,
    mapItem,
    queryable,
    debouncedQuery,
    orderable,
    orderDir,
  ]);

  /* ------------------------------ Request params ------------------------------ */
  // Builds the query string we send to the server for a given page
  const buildParamsString = useCallback(
    (pageNo, forcedQueryText) => {
      const filterExpr = buildFilter({
        displayField,
        queryText: queryable ? forcedQueryText ?? debouncedQuery : "",
        staticFilter,
      });
      const params = new URLSearchParams();
      params.set("page", String(pageNo));
      params.set("pagesize", String(pageSize));
      if (orderable) params.set("orderby", `${displayField} ${orderDir}`);
      if (filterExpr) params.set("filter", filterExpr);
      return params.toString();
    },
    [
      displayField,
      queryable,
      debouncedQuery,
      staticFilter,
      pageSize,
      orderable,
      orderDir,
    ]
  );

  // BufferService is only needed in remote mode and when an endpoint exists
  const svc = useMemo(() => {
    if (viewMode) return null;
    if (!endpoint) return null;
    return new BufferService(endpoint, baseUrl);
  }, [endpoint, baseUrl, viewMode]);

  /* ------------------------------ Global loading hook ------------------------- */
  // This syncs the internal loading state with the global loading overlay (if present)
  useEffect(() => {
    try {
      // New: prefer setIsLoad (same padrão da MTable), com fallback para APIs antigas
      if (typeof ctx?.setIsLoad === "function") {
        ctx.setIsLoad(loading);
      } else if (typeof ctx?.Loading === "function") {
        ctx.Loading(loading);
      } else if (typeof ctx?.setLoading === "function") {
        ctx.setLoading(loading);
      }
    } catch {
      /* no-op */
    }
  }, [loading, ctx]);

  /* ========================================================================== */
  /* Reset when scope changes                                                   */
  /* ========================================================================== */
  const pagesLoadedRef = useRef(1);
  const lastUnfilteredPagesRef = useRef(1);
  const prevQueryEmptyRef = useRef(true);
  const reportEnabledRef = useRef(false); // disables report/storage during programmatic loads

  // Whenever the effective scope changes we basically reset the local state
  useEffect(() => {
    lastScopeIdRef.current = effectiveScopeId;

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {
        /* ignore */
      }
      abortRef.current = null;
    }
    setItems([]);
    setPage(1);
    setHasMore(true);
    setErrorMsg("");
    setLoading(false);
    setQuery(""); // will be hydrated (initialState or storage) once
    setOrderDir(initialOrderDir === "desc" ? "desc" : "asc");
    pagesLoadedRef.current = 1;
    lastUnfilteredPagesRef.current = 1;
    prevQueryEmptyRef.current = true;
    requestTokenRef.current = 0;
    appliedInitialRef.current = false;
    reportEnabledRef.current = false;
  }, [effectiveScopeId, initialOrderDir]);

  /* ========================================================================== */
  /* Apply initialState ONCE per scope (authoritative only on first mount)      */
  /* ========================================================================== */
  // If the caller passes an initialState, we hydrate from there only once per scope
  useEffect(() => {
    if (!effectiveScopeId) return;
    if (appliedInitialRef.current) return; // only once per scope
    if (!initialState) return;

    const q = initialState?.query ?? "";
    const dir = initialState?.orderDir === "desc" ? "desc" : "asc";
    const pages = Math.max(1, Number(initialState?.pages ?? 1));
    const initQueryEmpty = q.trim() === "";

    reportEnabledRef.current = false;
    appliedInitialRef.current = true;

    setOrderDir(dir);
    setQuery(q);
    pagesLoadedRef.current = pages;
    lastUnfilteredPagesRef.current = initQueryEmpty ? pages : 1;
    prevQueryEmptyRef.current = initQueryEmpty;

    (async () => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      for (let p = 1; p <= pages; p++) {
        const r = await fetchPageNumber(p, p === 1, q /* forced */);
        if (!r?.ok || r?.stop) {
          setPage(p);
          return;
        }
      }
      setPage(pages);
      reportEnabledRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveScopeId, initialState]);

  /* ========================================================================== */
  /* Internal persistence HYDRATE-ONCE (only if NO initialState)                */
  /* ========================================================================== */
  // If internal persistence is enabled and there is no initialState, we hydrate from storage
  useEffect(() => {
    if (!effectiveScopeId) return;
    if (appliedInitialRef.current) return; // already applied initialState or storage
    if (!enableInternalPersistence) return;
    if (initialState) return; // initialState takes precedence

    const key = `SelectableTileGrid::${effectiveScopeId}`;
    const stored = safeRead(storageRef.current, key);
    if (!stored) {
      reportEnabledRef.current = true;
      return;
    } // nothing to hydrate

    const q = stored?.query ?? "";
    const dir = stored?.orderDir === "desc" ? "desc" : "asc";
    const pages = Math.max(1, Number(stored?.pages ?? 1));
    const initQueryEmpty = q.trim() === "";

    reportEnabledRef.current = false;
    appliedInitialRef.current = true;

    setOrderDir(dir);
    setQuery(q);
    pagesLoadedRef.current = pages;
    lastUnfilteredPagesRef.current = initQueryEmpty ? pages : 1;
    prevQueryEmptyRef.current = initQueryEmpty;

    (async () => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      for (let p = 1; p <= pages; p++) {
        const r = await fetchPageNumber(p, p === 1, q /* forced */);
        if (!r?.ok || r?.stop) {
          setPage(p);
          return;
        }
      }
      setPage(pages);
      reportEnabledRef.current = true; // enable user-driven reporting afterwards
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveScopeId, enableInternalPersistence, initialState]);

  /* -------------------------- Low-level page fetcher -------------------------- */
  // This is the core function that hits the API for a specific page and merges the results
  const fetchPageNumber = useCallback(
    async (pageNo, replaceOnFirst = false, forcedQueryText) => {
      if (viewMode || !svc) return { ok: false, stop: true };

      const myToken = ++requestTokenRef.current;

      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {
          /* ignore */
        }
        abortRef.current = null;
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setErrorMsg("");

      try {
        const url = `?${buildParamsString(pageNo, forcedQueryText)}`;
        const res = await svc.filter(url, { signal: controller.signal });

        // Ignore late responses when a newer request has been triggered
        if (myToken !== requestTokenRef.current)
          return { ok: false, stop: true };

        const rawItems = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];
        const totalPages = Number(
          res?.TotalPages ??
          (Array.isArray(res?.Items)
            ? 1
            : rawItems.length < pageSize
              ? 1
              : pageNo + 1)
        );
        const mapped = rawItems.map(mapItem).filter((x) => x?.id != null);

        setItems((prev) => {
          // For the very first page we can optionally replace instead of append
          if (pageNo === 1 || replaceOnFirst) {
            if (
              prev.length === mapped.length &&
              prev.every((v, i) => v.id === mapped[i]?.id)
            ) {
              return prev;
            }
            return mapped;
          }
          // For >1 pages we append without duplicating ids
          const seen = new Set(prev.map((p) => p.id));
          const appended = mapped.filter((m) => !seen.has(m.id));
          if (appended.length === 0) return prev;
          return [...prev, ...appended];
        });

        const more = pageNo < totalPages && mapped.length > 0;
        setHasMore(more);
        return { ok: true, stop: !more, totalPages };
      } catch (err) {
        if (err?.name === "AbortError") return { ok: false, stop: true };
        setErrorMsg(
          typeof err?.message === "string" ? err.message : "Error loading data"
        );
        setHasMore(false);
        return { ok: false, stop: true };
      } finally {
        if (requestTokenRef.current === myToken) setLoading(false);
      }
    },
    [viewMode, svc, buildParamsString, pageSize, mapItem]
  );

  /* ------------------------- Keep "depth" across changes ---------------------- */
  // This keeps track of how many pages deep we have gone so we can refetch that same depth
  useEffect(() => {
    pagesLoadedRef.current = Math.max(1, page);
  }, [page]);

  const isQueryEmpty = debouncedQuery.trim() === "";

  // This tracks when the query becomes empty or non-empty so we can restore the previous depth
  useEffect(() => {
    const wasEmpty = prevQueryEmptyRef.current;
    const nowEmpty = isQueryEmpty;

    if (wasEmpty && !nowEmpty) {
      lastUnfilteredPagesRef.current = Math.max(1, pagesLoadedRef.current);
    } else if (!wasEmpty && nowEmpty) {
      pagesLoadedRef.current = Math.max(1, lastUnfilteredPagesRef.current);
    }

    prevQueryEmptyRef.current = nowEmpty;
  }, [isQueryEmpty]);

  /* ------------------ Batch refetch SAME depth on order/search ---------------- */
  // When order or query changes, we refetch the same number of pages to keep the "depth" consistent
  const batchRefetchSameCount = useCallback(async () => {
    if (viewMode || !svc) return;

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {
        /* ignore */
      }
      abortRef.current = null;
    }

    const count = Math.max(1, pagesLoadedRef.current);

    setLoading(true);
    setErrorMsg("");
    setHasMore(true);

    const myToken = ++requestTokenRef.current;
    const all = [];

    try {
      const requests = Array.from({ length: count }, (_, i) => i + 1).map(
        async (p) => {
          const url = `?${buildParamsString(p)}`;
          const res = await svc.filter(url);
          if (myToken !== requestTokenRef.current)
            return { mapped: [], totalPages: 0 };
          const rawItems = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res)
              ? res
              : [];
          const mapped = rawItems.map(mapItem).filter((x) => x?.id != null);
          const totalPages = Number(
            res?.TotalPages ??
            (Array.isArray(res?.Items)
              ? 1
              : rawItems.length < pageSize
                ? 1
                : p + 1)
          );
          return { mapped, totalPages };
        }
      );
      const results = await Promise.all(requests);
      if (myToken !== requestTokenRef.current) return;

      let finalTotalPages =
        results.reduce((acc, r) => Math.max(acc, r.totalPages || 0), 0) ||
        count;
      results.forEach((r) => {
        all.push(...r.mapped);
      });

      if (myToken !== requestTokenRef.current) return;

      const seen = new Set();
      const uniqueAll = [];
      for (const it of all) {
        if (!seen.has(it.id)) {
          seen.add(it.id);
          uniqueAll.push(it);
        }
      }

      setItems(uniqueAll);
      const effectivePages = Math.min(
        count,
        isFinite(finalTotalPages) ? finalTotalPages : count
      );
      setPage(effectivePages);
      setHasMore(effectivePages < finalTotalPages);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setErrorMsg(
          typeof err?.message === "string" ? err.message : "Error loading data"
        );
        setHasMore(false);
      }
    } finally {
      if (requestTokenRef.current === myToken) setLoading(false);
    }
  }, [viewMode, svc, buildParamsString, pageSize, mapItem]);

  useEffect(() => {
    if (viewMode) return;

    // If we just applied initialState/storage, reporting is disabled.
    // Skip one tick and let user interactions trigger future syncs.
    if (!reportEnabledRef.current) {
      reportEnabledRef.current = true;
      return;
    }

    batchRefetchSameCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDir, debouncedQuery, staticFilter, viewMode]);

  /* ------------------------------ Initial fetching ---------------------------- */
  // On the very first mount (without initialState/storage), we fetch the first page
  useEffect(() => {
    if (viewMode) return;
    if (!endpoint) return;
    if (appliedInitialRef.current) return; // initialState or storage already applied

    (async () => {
      const r = await fetchPageNumber(1, true);
      if (r?.ok) {
        setPage(1);
        setHasMore(r.totalPages ? 1 < r.totalPages : true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, baseUrl, viewMode, effectiveScopeId]);

  /* --------------------- Report & WRITE persistence (debounced) --------------- */
  const lastReportedRef = useRef({ orderDir, query, pages: 1 });
  const reportTimerRef = useRef(null);

  // This snapshot represents the external state we want to expose and/or persist
  const snapshot = useMemo(
    () => ({
      orderDir,
      query,
      pages: Math.max(1, pagesLoadedRef.current || page || 1),
    }),
    [orderDir, query, page]
  );

  // Debounced reporting to parent and storage so we do not spam on every keystroke
  useEffect(() => {
    const prev = lastReportedRef.current;
    const changed = !(
      prev.orderDir === snapshot.orderDir &&
      prev.query === snapshot.query &&
      prev.pages === snapshot.pages
    );

    if (!changed) return;

    if (reportTimerRef.current) clearTimeout(reportTimerRef.current);
    reportTimerRef.current = setTimeout(() => {
      lastReportedRef.current = snapshot;

      // Parent callback
      try {
        typeof onStateChange === "function" && onStateChange(snapshot);
      } catch { }

      // Internal persistence WRITE (only when enabled)
      if (enableInternalPersistence && effectiveScopeId) {
        const key = `SelectableTileGrid::${effectiveScopeId}`;
        safeWrite(storageRef.current, key, snapshot);
      }
    }, 250);

    return () => {
      if (reportTimerRef.current) clearTimeout(reportTimerRef.current);
    };
  }, [snapshot, onStateChange, enableInternalPersistence, effectiveScopeId]);

  /* -------------------------- Component unmount cleanup ----------------------- */
  // On unmount we make sure pending timers and requests are cleaned up
  useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort();
      } catch {
        /* ignore */
      }
      if (reportTimerRef.current) clearTimeout(reportTimerRef.current);
    };
  }, []);

  /* -------------------------------- Handlers --------------------------------- */
  // Toggle between ascending and descending order
  const onToggleDir = useCallback(() => {
    setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
  }, []);

  // Local search input handler (accepts both string and event)
  const onSearchChange = useCallback((e) => {
    const val = typeof e === "string" ? e : e?.target?.value ?? "";
    setQuery(val);
  }, []);

  // Load the next page from the server when not in viewMode
  const onLoadMore = useCallback(async () => {
    if (viewMode) return;
    if (loading || !hasMore) return;
    const next = page + 1;
    const r = await fetchPageNumber(next);
    if (r.ok) {
      setPage(next);
      pagesLoadedRef.current = next;
    }
  }, [viewMode, loading, hasMore, page, fetchPageNumber]);

  // Toggle a single item and delegate the decision to the parent via onItemToggle
  const handleToggle = useCallback(
    (item) => {
      const nextChecked = !selectedSet.has(item.id);
      if (typeof onItemToggle === "function") {
        onItemToggle(item.raw ?? item, nextChecked);
      }
    },
    [onItemToggle, selectedSet]
  );

  /* ------------------------- Data to render (view vs fetch) ------------------- */
  // renderList is the raw list depending on mode: either local items or remote ones
  const renderList = useMemo(() => {
    if (viewMode) return localList;
    return items;
  }, [viewMode, localList, items]);

  /* ------------------------ Visible list (whitelist aware) -------------------- */
  // visibleList is what we actually show; it respects the whitelist only in remote mode
  const visibleList = useMemo(() => {
    // In view mode we keep the original list and ignore the whitelist for visibility
    if (viewMode) return renderList;

    // No whitelist means we show everything we received
    if (selectableSet === null) return renderList;

    // When a whitelist exists, we only show items that are actually allowed
    return renderList.filter((it) => selectableSet.has(it.id));
  }, [viewMode, renderList, selectableSet]);

  /* ------------------------------ Layout numbers ----------------------------- */
  // This basically determines how tall the container wants to be by default
  const autoMinHeight = useMemo(
    () =>
      computeAutoMinHeight({
        pageSize,
        tileHeight: effectiveTileHeight,
        gridGap,
        showToolbar,
      }),
    [pageSize, effectiveTileHeight, gridGap, showToolbar]
  );

  const containerMinHeight = minHeight ?? autoMinHeight;

  /* ----------------------------------- UI ------------------------------------ */
  return (
    <div
      className={`stg-container ${align === "vertical" ? "stg--vertical" : ""}`}
      style={{
        width,
        minHeight: containerMinHeight,
        ...(maxHeight ? { maxHeight } : null),
      }}
    >
      {/* Title */}
      {title && <span className="stg-title">{title}</span>}

      {/* Toolbar (independent toggles) */}
      {(showSearch || showOrder) && (
        <div className="stg-toolbar">
          <div className="stg-toolbar-left">
            {showSearch && (
              <div className="stg-search-wrapper">
                <MInputTextField
                  label={t("Search")}
                  placeholder={searchPlaceholder || t("Search")}
                  value={query}
                  onChange={onSearchChange}
                  size="100%"
                />
              </div>
            )}
          </div>

          <div className="stg-toolbar-right">
            {showOrder && (
              <MButton
                onClick={onToggleDir}
                label={t("Order")}
                icon={
                  <i
                    className={`pi ${orderDir === "asc" ? "pi-arrow-down" : "pi-arrow-up"
                      }`}
                  />
                }
                backgroundColor="var(--color-white)"
                labelColor="var(--color-red)"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "center",
                  maxWidth: 300,
                  minWidth: 120,
                }}
                height={8}
                width={20}
                tooltip={t("Order")}
              />
            )}
          </div>
        </div>
      )}

      {/* List/Grid */}
      <div
        className="stg-grid"
        style={{
          gap: gridGap,
        }}
      >
        {visibleList.map((it) => {
          const isChecked = viewMode ? true : selectedSet.has(it.id);

          // Here we only deal with disabled state, visibility is already handled by visibleList
          return (
            <Tile
              key={it.id}
              label={it.label}
              checked={isChecked}
              disabled={disabled || (viewMode ? true : false)}
              onToggle={() => handleToggle(it)}
              tileHeight={effectiveTileHeight}
            />
          );
        })}

        {/* Load More row */}
        {/* Keep the row rendered even while loading so the layout does not jump on the first click */}
        {!viewMode && hasMore && visibleList.length > 0 && (
          <div className="stg-loadmore">
            <MButton
              icon={<i className="pi pi-search" />}
              label={loading ? t("Loading") : t("LoadMore")}
              onClick={onLoadMore}
              // Do not show tooltip while the button is disabled
              tooltip={loading ? undefined : t("LoadNextPage")}
              backgroundColor="transparent"
              borderColor="transparent"
              labelColor="var(--color-red)"
              hoverEffect
              marginNone
              normal
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Empty */}
      {!loading && visibleList.length === 0 && !errorMsg && (
        <div className="stg-empty">{t("NoRecordsFound")}</div>
      )}

      {/* Error */}
      {errorMsg && (
        <div className="stg-error">
          {t("Error")}: {errorMsg}
        </div>
      )}
    </div>
  );
}
